import * as React from "react";
import {
  makeStyles,
  shorthands,
  mergeClasses,
  Spinner,
} from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  ArrowRight20Regular,
  Send20Filled,
} from "@fluentui/react-icons";
import StepIndicator from "../components/schadenmeldung/StepIndicator";
import {
  FONT_ACCENT,
  FONT_BODY,
  lilaTheme,
  themeVars,
} from "../components/schadenmeldung/theme";
import {
  pflichtErfuellt,
  schrittListe,
} from "../components/schadenmappe/meldung/schritte";
import { BIG_COPY } from "../components/schadenmeldung-big/copy";
import {
  initialBigAnswers,
  type BigAnswers,
  type BigStepKey,
} from "../components/schadenmeldung-big/types";
import {
  submitBigSchadenmeldung,
  uploadSchadenmeldungFile,
} from "../components/schadenmeldung-big/api";
import { PhotoProvider } from "../components/schadenmeldung-big/PhotoUpload";
import {
  StepType,
  MelderStep,
  WhenStep,
  UnterlagenStep,
  AbschlussStep,
} from "../components/schadenmeldung-big/steps/shared";
import {
  GebWhere,
  GebArt,
  GebHergang,
  GebMassnahmen,
  GebVerursacher,
  GebAbrechnung,
} from "../components/schadenmeldung-big/steps/gebaeude";
import {
  KfzSchaeden,
  KfzWhere,
  KfzArt,
  KfzEigenes,
  KfzFremdes,
  KfzHergang,
  KfzPerson,
  KfzSachen,
} from "../components/schadenmeldung-big/steps/kfz";
import {
  SonBetroffen,
  SonWhere,
  SonDetails,
} from "../components/schadenmeldung-big/steps/sonstiges";
import SummaryStep from "../components/schadenmeldung-big/steps/SummaryStep";
import DoneStep from "../components/schadenmeldung-big/steps/DoneStep";

const useStyles = makeStyles({
  page: {
    minHeight: "100vh",
    width: "100%",
    backgroundColor: "var(--sm-page-bg)",
    backgroundImage: "var(--sm-page-gradient)",
    color: "var(--sm-on-dark)",
    fontFamily: FONT_BODY,
    boxSizing: "border-box",
    // Platz für die feste Leiste unten (Höhe + Home-Indikator am Telefon).
    paddingBottom: "calc(110px + env(safe-area-inset-bottom, 0px))",
  },
  topbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    maxWidth: "1080px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingTop: "20px",
    paddingBottom: "12px",
    paddingLeft: "20px",
    paddingRight: "20px",
  },
  brand: { display: "flex", flexDirection: "column", gap: "2px" },
  brandLine: {
    fontFamily: FONT_BODY,
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "var(--sm-accent)",
  },
  brandTitle: {
    fontFamily: FONT_ACCENT,
    fontSize: "1.6rem",
    color: "var(--sm-on-dark)",
    margin: 0,
    fontWeight: 500,
  },
  indicatorWrap: {
    maxWidth: "1080px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingLeft: "20px",
    paddingRight: "20px",
    paddingBottom: "18px",
  },
  cardShell: {
    maxWidth: "1080px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingLeft: "20px",
    paddingRight: "20px",
  },
  card: {
    backgroundColor: "var(--sm-card-bg)",
    color: "var(--sm-ink)",
    paddingTop: "28px",
    paddingBottom: "28px",
    paddingLeft: "24px",
    paddingRight: "24px",
    ...shorthands.borderRadius("18px"),
    boxShadow: "0 24px 60px rgba(0,0,0,0.32)",
    "@media (min-width: 720px)": {
      paddingTop: "40px",
      paddingBottom: "40px",
      paddingLeft: "44px",
      paddingRight: "44px",
    },
  },
  /** Zurück/Weiter als feste, deckende Leiste — vorher schwebten die Knöpfe
      halbtransparent über dem Formular und verdeckten den Text darunter. */
  footer: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "var(--sm-primary-deep)",
    ...shorthands.borderTop("1px", "solid", "rgba(255,255,255,0.14)"),
    boxShadow: "0 -10px 30px rgba(0,0,0,0.28)",
    paddingTop: "12px",
    paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
    paddingLeft: "16px",
    paddingRight: "16px",
    zIndex: 20,
  },
  footerInner: {
    maxWidth: "1080px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
  },
  navBtn: {
    appearance: "none",
    cursor: "pointer",
    fontFamily: FONT_BODY,
    fontSize: "0.98rem",
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "18px",
    paddingRight: "18px",
    ...shorthands.borderRadius("12px"),
    transitionProperty: "background-color, color, transform, box-shadow",
    transitionDuration: "160ms",
    // Am Telefon teilen sich beide Knöpfe die Leiste.
    "@media (max-width: 720px)": { flexGrow: 1, flexBasis: 0, justifyContent: "center" },
  },
  backBtn: {
    backgroundColor: "rgba(255,255,255,0.08)",
    color: "var(--sm-on-dark)",
    ...shorthands.border("1px", "solid", "rgba(255,255,255,0.20)"),
    ":hover": { backgroundColor: "rgba(255,255,255,0.16)" },
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  forwardBtn: {
    backgroundColor: "var(--sm-accent)",
    color: "var(--sm-primary-deep)",
    ...shorthands.border("1px", "solid", "var(--sm-accent)"),
    boxShadow: "0 12px 26px rgba(247,168,35,0.35)",
    ":hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 14px 32px rgba(247,168,35,0.50)",
    },
    ":disabled": {
      backgroundColor: "rgba(255,255,255,0.10)",
      color: "var(--sm-on-dark-muted)",
      ...shorthands.borderColor("rgba(255,255,255,0.18)"),
      boxShadow: "none",
      cursor: "not-allowed",
      transform: "none",
    },
  },
});

export default function PublicSchadenmeldungBig() {
  const styles = useStyles();
  // Fest Lila — der Umschalter Lila/Blau ist seit 11.10.2026 entfernt.
  const theme = lilaTheme;

  const [answers, setAnswers] = React.useState<BigAnswers>(initialBigAnswers);
  const [stepKey, setStepKey] = React.useState<BigStepKey>("type");
  const [visitedMax, setVisitedMax] = React.useState<number>(0);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [referenceCode, setReferenceCode] = React.useState<string | null>(null);
  const [photos, setPhotos] = React.useState<File[]>([]);

  const photoCtx = React.useMemo(
    () => ({
      files: photos,
      addFiles: (fs: File[]) => setPhotos((prev) => [...prev, ...fs]),
      removeFile: (i: number) => setPhotos((prev) => prev.filter((_, j) => j !== i)),
    }),
    [photos],
  );

  const order = React.useMemo(() => schrittListe(answers), [answers]);
  const currentIndex = Math.max(0, order.indexOf(stepKey));

  React.useEffect(() => {
    setVisitedMax((m) => Math.max(m, currentIndex));
  }, [currentIndex]);

  const update = React.useCallback((patch: Partial<BigAnswers>) => {
    setAnswers((prev) => ({ ...prev, ...patch }));
  }, []);

  const indicatorSteps = order
    .filter((k) => k !== "done")
    .map((k) => ({ key: k, label: BIG_COPY.steps[k] ?? k }));

  const goBack = () => {
    const i = order.indexOf(stepKey);
    if (i > 0) setStepKey(order[i - 1]);
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await submitBigSchadenmeldung(answers);
      setReferenceCode(res.referenceCode);
      // Upload any photos picked during the form (best-effort).
      if (photos.length > 0) {
        await Promise.allSettled(
          photos.map((f) => uploadSchadenmeldungFile(res.referenceCode, f)),
        );
      }
      setStepKey("done");
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : BIG_COPY.errors.submit);
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = async () => {
    if (stepKey === "summary") {
      await submit();
      return;
    }
    const i = order.indexOf(stepKey);
    if (i >= 0 && i < order.length - 1) setStepKey(order[i + 1]);
  };

  const jumpTo = (target: BigStepKey) => {
    const i = order.indexOf(target);
    if (i >= 0 && i <= visitedMax + 1) setStepKey(target);
  };

  const restart = () => {
    setAnswers(initialBigAnswers);
    setStepKey("type");
    setVisitedMax(0);
    setSubmitError(null);
    setReferenceCode(null);
    setPhotos([]);
  };

  const isDone = stepKey === "done";
  const advanceLabel = stepKey === "summary" ? BIG_COPY.nav.submit : BIG_COPY.nav.next;
  const canGoForward = isDone ? true : pflichtErfuellt(stepKey, answers, { art: "oeffentlich" });

  const stepProps = { answers, update };

  return (
    <PhotoProvider value={photoCtx}>
    <div className={styles.page} style={themeVars(theme)}>
      <div className={styles.topbar}>
        <div className={styles.brand}>
          <span className={styles.brandLine}>{BIG_COPY.hero.eyebrow}</span>
          <h2 className={styles.brandTitle}>{BIG_COPY.brandLine}</h2>
        </div>
      </div>

      {!isDone && (
        <div className={styles.indicatorWrap}>
          <StepIndicator
            steps={indicatorSteps}
            currentIndex={currentIndex}
            visitedCount={visitedMax}
            onJump={(idx) => jumpTo(order[idx])}
          />
        </div>
      )}

      <div className={styles.cardShell}>
        <div className={styles.card}>
          {stepKey === "type" && <StepType {...stepProps} />}

          {stepKey === "geb_melder" && <MelderStep {...stepProps} />}
          {stepKey === "geb_when" && <WhenStep {...stepProps} />}
          {stepKey === "geb_where" && <GebWhere {...stepProps} />}
          {stepKey === "geb_art" && <GebArt {...stepProps} />}
          {stepKey === "geb_hergang" && <GebHergang {...stepProps} />}
          {stepKey === "geb_massnahmen" && <GebMassnahmen {...stepProps} />}
          {stepKey === "geb_verursacher" && <GebVerursacher {...stepProps} />}
          {stepKey === "geb_abrechnung" && <GebAbrechnung {...stepProps} />}
          {stepKey === "geb_unterlagen" && <UnterlagenStep {...stepProps} />}

          {stepKey === "kfz_schaeden" && <KfzSchaeden {...stepProps} />}
          {stepKey === "kfz_melder" && <MelderStep {...stepProps} />}
          {stepKey === "kfz_when" && <WhenStep {...stepProps} />}
          {stepKey === "kfz_where" && <KfzWhere {...stepProps} />}
          {stepKey === "kfz_art" && <KfzArt {...stepProps} />}
          {stepKey === "kfz_eigenes" && <KfzEigenes {...stepProps} />}
          {stepKey === "kfz_fremdes" && <KfzFremdes {...stepProps} />}
          {stepKey === "kfz_hergang" && <KfzHergang {...stepProps} />}
          {stepKey === "kfz_person" && <KfzPerson {...stepProps} />}
          {stepKey === "kfz_sachen" && <KfzSachen {...stepProps} />}
          {stepKey === "kfz_unterlagen" && <UnterlagenStep {...stepProps} />}

          {stepKey === "son_betroffen" && <SonBetroffen {...stepProps} />}
          {stepKey === "son_melder" && <MelderStep {...stepProps} />}
          {stepKey === "son_when" && <WhenStep {...stepProps} />}
          {stepKey === "son_where" && <SonWhere {...stepProps} />}
          {stepKey === "son_details" && <SonDetails {...stepProps} />}
          {stepKey === "son_unterlagen" && <UnterlagenStep {...stepProps} />}

          {stepKey === "abschluss" && <AbschlussStep {...stepProps} />}
          {stepKey === "summary" && (
            <SummaryStep
              answers={answers}
              jumpTo={jumpTo}
              submitting={submitting}
              submitError={submitError}
            />
          )}
          {stepKey === "done" && referenceCode && (
            <DoneStep answers={answers} referenceCode={referenceCode} onRestart={restart} />
          )}
        </div>
      </div>

      {!isDone && (
        <div className={styles.footer}>
          <div className={styles.footerInner}>
            <button
              type="button"
              className={mergeClasses(styles.navBtn, styles.backBtn)}
              onClick={goBack}
              disabled={currentIndex === 0 || submitting}
            >
              <ArrowLeft20Regular />
              {BIG_COPY.nav.back}
            </button>
            <button
              type="button"
              className={mergeClasses(styles.navBtn, styles.forwardBtn)}
              onClick={goNext}
              disabled={!canGoForward || submitting}
            >
              {submitting ? (
                <Spinner size="tiny" appearance="inverted" />
              ) : stepKey === "summary" ? (
                <Send20Filled />
              ) : null}
              {advanceLabel}
              {!submitting && stepKey !== "summary" && <ArrowRight20Regular />}
            </button>
          </div>
        </div>
      )}
    </div>
    </PhotoProvider>
  );
}
